import { Pagination } from '@/components/table/Paginate';
import {
  Box,
  Table as ChakraTable,
  Flex,
  Select,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
  Text,
  Skeleton,
  Icon,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Sort } from './sort';
import { dotGet } from './dotGet';
import { EmptyReport } from '@/pages/reports/non-ats-reports/shared/empty-report';
import { ChevronRightIcon } from '@heroicons/react/outline';
import { logicalSort } from '@/pages/reports/non-ats-reports/shared/sort/logicalSort';
import { useHover } from '@/pages/reports/non-ats-reports/shared/hooks/useHover';
import { TruncatedTextWithTooltip } from '@/ui/TooltipText';

const sortData = (data, key, direction) => {
  return [...data].sort((a, b) => {
    const valA = dotGet(a, key);
    const valB = dotGet(b, key);
    return logicalSort(valA, valB, direction);
  });
};

const Header = ({ sticky, sortable, name, columnKey, sort, setSort }) => {
  const { hover, hoverProps } = useHover();
  return (
    <Th
      {...(sticky
        ? {
            position: 'sticky',
            minWidth: '164px',
            maxWidth: '164px',
            left: 0,
            backgroundColor: '#FFFFFF',
          }
        : {})}
      border="1px solid #EBEBEC"
      padding="12px 16px"
      {...hoverProps}
      onClick={() => {
        if (sort[0] !== columnKey || sort[1] === 'default') {
          setSort([columnKey, 'desc']);
        } else if (sort[1] === 'desc') {
          setSort([columnKey, 'asc']);
        } else {
          setSort([columnKey, 'default']);
        }
      }}
    >
      <Flex
        alignItems="center"
        justifyContent={sticky ? 'flex-start' : 'center'}
      >
        <Text
          lineHeight="20px"
          textTransform="capitalize"
          fontWeight="medium"
          letterSpacing="0.01em"
          color="#5E5E5E"
          overflow="hidden"
          textOverflow="ellipsis"
        >
          {name}
        </Text>
        {sortable ? (
          <Sort
            direction={sort[0] === columnKey ? sort[1] : 'default'}
            marginLeft={sticky ? 'auto' : '8px'}
            visibility={
              hover || (sort[0] === columnKey && sort[1] !== 'default')
                ? 'visible'
                : 'hidden'
            }
          />
        ) : null}
      </Flex>
    </Th>
  );
};

export const Table = ({
  data = [],
  columns = [],
  isLoading = false,
  showPerPage = true,
  defaultSort = [],
  expand = {},
}) => {
  const [perPage, setPerPage] = useState(5);
  const [currentPage, setCurrentPage] = useState(0);

  const totalPages = useMemo(() => {
    return Math.ceil(data.length / perPage);
  }, [perPage, data.length]);

  useEffect(() => {
    setCurrentPage(0);
  }, [data]);

  const [sort, setSort] = useState(defaultSort);

  const paginatedData = useMemo(() => {
    if (isLoading) {
      return [...Array(perPage).keys()].map(() => [
        ...Array(columns.length).keys(),
      ]);
    }

    const sortedData = sort[1] !== 'default' ? sortData(data, ...sort) : data;
    return sortedData.slice(currentPage * perPage, (currentPage + 1) * perPage);
  }, [perPage, currentPage, data, sort, isLoading, columns.length]);

  const { canRowExpand, onExpand } = expand;
  const [expandedRows, setExpandedRows] = useState([]);
  const toggleExpandedRow = useCallback(
    (rowInx) => {
      if (expandedRows.includes(rowInx)) {
        setExpandedRows(expandedRows.filter((x) => x !== rowInx));
      } else {
        setExpandedRows([...expandedRows, rowInx]);
      }
    },
    [expandedRows]
  );

  useEffect(() => {
    if (isLoading) {
      setExpandedRows([]);
    }
  }, [isLoading]);

  if (!isLoading && data.length === 0) {
    return <EmptyReport />;
  }

  return (
    <Box>
      <TableContainer>
        <ChakraTable>
          <Thead>
            <Tr>
              {columns.map((column, inx) => (
                <Header
                  {...column}
                  columnKey={column.key}
                  sort={sort}
                  setSort={setSort}
                  key={`row-${inx}`}
                />
              ))}
            </Tr>
          </Thead>
          <Tbody>
            {paginatedData.map((row, rowInx) => {
              const canExpand = canRowExpand?.(row, rowInx);
              const isExpanded = expandedRows.includes(rowInx);
              return (
                <>
                  <Tr key={`row-${rowInx}`}>
                    {columns.map(
                      ({ key, sticky, render, style = {} }, colInx) => {
                        const value = dotGet(row, key);
                        return (
                          <Td
                            {...(sticky
                              ? {
                                  position: 'sticky',
                                  left: 0,
                                  backgroundColor: '#FFFFFF',
                                  minWidth: '164px',
                                  maxWidth: '164px',
                                }
                              : {})}
                            border="1px solid #EBEBEC"
                            padding="12px 16px"
                            key={`col-${colInx}`}
                          >
                            <Flex
                              alignItems="center"
                              justifyContent={sticky ? 'unset' : 'center'}
                            >
                              {!isLoading && canExpand && colInx === 0 ? (
                                <Icon
                                  as={ChevronRightIcon}
                                  transform={`rotate(${
                                    isExpanded ? '90deg' : '0deg'
                                  })`}
                                  transition="all 0.2s ease"
                                  display="inline"
                                  mr="4px"
                                  cursor="pointer"
                                  onClick={() => toggleExpandedRow(rowInx)}
                                />
                              ) : null}
                              {isLoading ? (
                                <Skeleton
                                  height="8px"
                                  borderRadius="8px"
                                  marginY="6px"
                                  marginX="auto"
                                  flexGrow={1}
                                  maxWidth="120px"
                                />
                              ) : typeof render === 'function' ? (
                                render(value, row)
                              ) : (
                                <TruncatedTextWithTooltip
                                  text={value}
                                  fontSize="14px"
                                  lineHeight="24px"
                                  color="#161616"
                                  fontWeight={canExpand ? 700 : 500}
                                  letterSpacing="0.01em"
                                  overflow="hidden"
                                  textOverflow="ellipsis"
                                  textAlign={sticky ? 'left' : 'center'}
                                  {...style}
                                />
                              )}
                            </Flex>
                          </Td>
                        );
                      }
                    )}
                  </Tr>
                  {isExpanded ? onExpand?.(row, rowInx) : null}
                </>
              );
            })}
          </Tbody>
        </ChakraTable>
      </TableContainer>

      <Flex mt="16px">
        {showPerPage ? (
          <Select
            width="auto"
            bgColor="white"
            borderRadius="sm"
            onChange={(event) => {
              setPerPage(event.target.value);
              setCurrentPage(0);
            }}
          >
            <option value={5}>Show 5</option>
            <option value={10}>Show 10</option>
          </Select>
        ) : null}
        {totalPages > 1 ? (
          <Box ml="auto">
            <Pagination
              currPage={currentPage}
              totalPage={totalPages}
              handlePageClick={({ selected }) => setCurrentPage(selected)}
            />
          </Box>
        ) : null}
      </Flex>
    </Box>
  );
};
